import { 
    ActiveSession, 
    PerformanceBlock, 
    PerformanceBlockItem,
    PerformanceLog, 
    ExerciseGroup 
} from '@/types/app.types';

export interface SystemStep {
    blockId: string | null;
    itemId: string | null;
    round: number;
}

/**
 * calculateProgress: Computes global completion percentage.
 */
export const calculateProgress = (session: ActiveSession | null) => {
    if (!session || !session.blocks) return { totalTarget: 0, totalLogged: 0, percent: 0 };
    let totalTarget = 0;
    let totalLogged = 0;
    session.blocks.forEach(block => {
        (block.items || []).forEach(item => {
            const target = typeof item.target_sets === 'string' ? parseInt(item.target_sets) : (item.target_sets || 3);
            const logged = (session.logs[item.id] || []).length;
            totalTarget += target;
            totalLogged += Math.min(logged, target);
        });
    });
    const percent = totalTarget > 0 ? (totalLogged / totalTarget) * 100 : 0;
    return { totalTarget, totalLogged, percent };
};

/**
 * findFirstIncomplete: Locates next focus item.
 */
export const findFirstIncomplete = (session: ActiveSession) => {
    let targetBlockId = session.blocks[0]?.id || null;
    let targetItemId = session.blocks[0]?.items[0]?.id || null;
    for (const b of session.blocks) {
        const firstIncomplete = b.items.find(item => {
            const logs = session.logs[item.id] || [];
            const target = typeof item.target_sets === 'string' ? parseInt(item.target_sets) : (item.target_sets || 3);
            return logs.length < target;
        });
        if (firstIncomplete) {
            targetBlockId = b.id;
            targetItemId = firstIncomplete.id;
            break;
        }
    }
    return { blockId: targetBlockId, itemId: targetItemId };
};

/**
 * calculateNextStep: Focus-shifting engine.
 */
export const calculateNextStep = (
    session: ActiveSession, 
    blockId: string, 
    itemId: string, 
    currentRound: number, 
    isCircuit: boolean,
    updatedLogs: Record<string, any[]>
): SystemStep | null => {
    const block = session.blocks.find(b => b.id === blockId);
    if (!block) return null;
    const itemIdx = block.items.findIndex(i => i.id === itemId);
    const item = block.items[itemIdx];
    const target = typeof item.target_sets === 'string' ? parseInt(item.target_sets) : (item.target_sets || 3);
    if (isCircuit) {
        if (itemIdx < block.items.length - 1) return { blockId, itemId: block.items[itemIdx + 1].id, round: currentRound };
        else if (currentRound < target) return { blockId, itemId: block.items[0].id, round: currentRound + 1 };
    } else if (updatedLogs[itemId].length >= target) {
        if (itemIdx < block.items.length - 1) return { blockId, itemId: block.items[itemIdx + 1].id, round: 1 };
    }
    return null;
};

/**
 * injectAdHocBlock: Dynamically add volume/exercises.
 */
export const injectAdHocBlock = (session: ActiveSession, exercise: any): ActiveSession => {
    const exerciseId = exercise.id;
    if (!exerciseId) return session;
    let found = false;
    const updatedBlocks = session.blocks.map(block => {
        const updatedItems = block.items.map(item => {
            if (item.exercise_library_id === exerciseId) {
                found = true;
                const currentSets = parseInt(String(item.target_sets || 0)) || 0;
                return { ...item, target_sets: currentSets + 1 };
            }
            return item;
        });
        return { ...block, items: updatedItems };
    });
    if (found) return { ...session, blocks: updatedBlocks };
    let adHocBlock = updatedBlocks.find(b => b.id === 'adhoc-block');
    const otherBlocks = updatedBlocks.filter(b => b.id !== 'adhoc-block');
    if (!adHocBlock) {
        adHocBlock = { id: 'adhoc-block', label: 'AD-HOC', block_type: 'LINEAR', items: [], sort_order: 999 };
    }
    const newItem: PerformanceBlockItem = {
        id: `adhoc-${exerciseId}`,
        name: exercise.name || 'Unknown Exercise',
        exercise_library_id: exerciseId,
        target_sets: 1, target_reps: "10", target_weight: "0", target_rpe: "8", tempo: "2020",
        metric_type: 'RESISTANCE', sort_order: adHocBlock.items.length
    };
    return { ...session, blocks: [...otherBlocks, { ...adHocBlock, items: [...adHocBlock.items, newItem] }] };
};

/**
 * reconstructSessionFromLogs: Forensic volume restoration.
 */
export const reconstructSessionFromLogs = (session: ActiveSession, existingLogs: any[]): ActiveSession => {
    const updatedLogs: Record<string, any[]> = {};
    let updatedSession = { ...session };
    existingLogs.forEach(log => {
        const allItems = updatedSession.blocks.flatMap(b => b.items);
        const blockItem = allItems.find(i => (log.block_item_id && i.id === log.block_item_id) || (!log.block_item_id && i.exercise_library_id === log.exercise_library_id));
        const targetId = blockItem?.id || `adhoc-${log.exercise_library_id}`;
        if (!updatedLogs[targetId]) updatedLogs[targetId] = [];
        updatedLogs[targetId].push({ id: log.id, weight: log.weight, reps: log.reps, rpe: log.rpe, duration_seconds: log.duration_seconds, distance_meters: log.distance_meters });
        if (!blockItem) {
            updatedSession = injectAdHocBlock(updatedSession, { id: log.exercise_library_id, name: log.exercise_name_snapshot || 'Unknown Exercise' });
        } else {
            const currentLogged = updatedLogs[targetId].length;
            const currentTarget = parseInt(String(blockItem.target_sets || 0)) || 0;
            if (currentLogged > currentTarget) {
                updatedSession.blocks = updatedSession.blocks.map(b => ({
                    ...b,
                    items: b.items.map(i => i.id === blockItem.id ? { ...i, target_sets: currentLogged } : i)
                }));
            }
        }
    });
    return { ...updatedSession, logs: updatedLogs };
};

/**
 * preparePerformanceLogsForInsert: Forensic DB preparation.
 */
export const preparePerformanceLogsForInsert = (
    sessionId: string, 
    sessionLogs: Record<string, PerformanceLog[]>, 
    sessionBlocks: PerformanceBlock[], 
    startTime: string
) => {
    const logsToInsert: any[] = [];
    Object.entries(sessionLogs).forEach(([itemId, sets]) => {
        let snapshotName = null; let snapshotTarget = null; let exerciseLibraryId = null;
        let isAdHoc = itemId.startsWith('adhoc-');
        for (const b of sessionBlocks) {
            const item = b.items.find(i => i.id === itemId);
            if (item) {
                snapshotName = item.name; exerciseLibraryId = item.exercise_library_id;
                snapshotTarget = isAdHoc ? 'AD-HOC' : (item.target_sets ? `${item.target_sets}x${item.target_reps} @ ${item.target_weight}` : null);
                break;
            }
        }
        sets.forEach((setEntry, index) => {
            let rpeVal = typeof setEntry.rpe === 'string' ? parseFloat(setEntry.rpe) : (setEntry.rpe || null);
            if (isNaN(rpeVal as number)) rpeVal = null;
            logsToInsert.push({
                completed_session_id: sessionId, block_item_id: isAdHoc ? null : itemId, exercise_library_id: exerciseLibraryId,
                exercise_name_snapshot: snapshotName, target_snapshot: snapshotTarget, weight: String(setEntry.weight || '0'),
                reps: parseInt(String(setEntry.reps)) || 0, rpe: rpeVal, duration_seconds: setEntry.duration_seconds || 0,
                distance_meters: setEntry.distance_meters || 0, set_number: index + 1, created_at: startTime, updated_at: startTime
            });
        });
    });
    return logsToInsert;
};

/**
 * Groups raw performance logs by exercise for hierarchical display.
 */
export const groupLogsByExercise = (logs: any[] = []): ExerciseGroup[] => {
    const groups: ExerciseGroup[] = [];
    logs.forEach(log => {
        const name = log.exercise_library?.name || log.exercise_name_snapshot || 'Unknown';
        const targets = {
            weight: log.block_items?.target_weight || log.target_snapshot || '?',
            reps: log.block_items?.target_reps || '?',
            rpe: log.block_items?.target_rpe || '?'
        };

        let group = groups.find(g => g.name === name);
        if (!group) {
            group = { name, targets, sets: [] };
            groups.push(group);
        }
        group.sets.push(log);
    });
    return groups;
};
